import { onBeforeMount, onBeforeUnmount, ref } from 'vue';
import { checkVersion, createVersionCheckerState } from '../utils/checkVersion';

export function useVersionChecker() {
    const isNewVersionAvailable = ref(false);
    let intervalId: ReturnType<typeof setInterval> | null = null;
    // Each checker owns its own state so two concurrent useVersionChecker
    // instances don't blind each other on the first true upgrade.
    const versionState = createVersionCheckerState();

    const performVersionCheck = () => {
        checkVersion(versionState, () => {
            isNewVersionAvailable.value = true;
        });
    };

    function startVersionCheck() {
        if (intervalId != null) {
            return;
        }

        performVersionCheck();
        // 30s base + up to 15s jitter to avoid synchronized polling across tabs/clients.
        const jitter = Math.floor(Math.random() * 15_000);
        intervalId = setInterval(performVersionCheck, 30_000 + jitter);
    }

    function stopVersionCheck() {
        if (intervalId != null) {
            clearInterval(intervalId);
            intervalId = null;
        }
    }

    function onVisibilityChange() {
        if (document.visibilityState === 'visible') {
            startVersionCheck();
        } else {
            stopVersionCheck();
        }
    }

    function onFocus() {
        // Window focus on the same tab doesn't always raise visibilitychange
        // (e.g., refocusing the browser window from another app). Poll once
        // so a returning user sees an upgrade prompt without waiting out the
        // interval.
        performVersionCheck();
    }

    onBeforeMount(() => {
        startVersionCheck();
        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('focus', onFocus);
    });

    onBeforeUnmount(() => {
        stopVersionCheck();
        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('focus', onFocus);
    });

    return {
        isNewVersionAvailable,
    };
}
